import React, { FC, useEffect, useState } from 'react';
import { Button, Checkbox, Drawer, Form, notification, Select, Space, TimePicker } from 'antd';
import EmployeeInput from 'features/absence/madalAddOneAbsence/employeeInput';
import { useLazyGetDivisionEmployeesQuery } from 'app/services/edpApiV1/viewer';
import { TDivisionEmployees } from 'shared/types/types';
import { RvdCompensationType, RvdStatuses } from 'shared/enums/rvd';
import { PICKER_DATE_MASK, PICKER_TIME_MASK, SERVER_DATE_MASK } from 'shared/constants';
import { TRvdGetResponse, TUserRvdInfo } from 'shared/types/rvd.types';
import { RVD_FORM_FIELDS } from 'shared/constants/rvd';
import { momentFormatCheck } from 'shared/helpers/type-check';
import { disabledDateTime, getPrettiedHoursAndMinutes } from 'shared/helpers/date';
import dayjs, { Dayjs } from 'dayjs';
import DatePickerWithDots from 'shared/components/datePickerWithDots/DatePickerWithDots';
import TimeRangePickerWithDots from 'shared/components/timeRangePickerWithDots/TimeRangePickerWithDots';
import style from './index.module.scss';

type TProps = {
  onClose: () => void;
  isOpen: boolean;
  onOk: (request: TUserRvdInfo) => void;
  currentRvd?: TRvdGetResponse;
  currentUser?: TUserRvdInfo;
  remove?: (id: number | string) => void;
};

type TFormData = {
  brakeTime: [Dayjs, Dayjs];
  type: RvdCompensationType;
  employeeId: number;
  isOnVacation: boolean;
  isSecondBrake: boolean;
  toDate: Dayjs;
  workTime: [Dayjs, Dayjs];
};

const RvdSidebar: FC<TProps> = ({ onClose, isOpen, onOk, currentRvd, currentUser, remove }) => {
  const [form] = Form.useForm();
  const [employee, setEmployee] = useState<TDivisionEmployees | null>(null);
  const [getEmployees, resultEmployees] = useLazyGetDivisionEmployeesQuery();
  const [workDayDuration, setWorkDayDuration] = useState<string | null>(null);

  const submitForm = () => {
    form.validateFields().then(() => {
      form.submit();
      setWorkDayDuration(null);
    });
  };

  const onCloseSidebar = () => {
    setWorkDayDuration(null);
    onClose();
  };

  const validateBrakeField = () => {
    const workingHours = dayjs(workDayDuration, 'HH:mm').hour();
    return Number(workingHours) >= 4;
  };

  const calcDateDiff = (dates: unknown) => {
    if (!dates) {
      setWorkDayDuration(null);
      return;
    }
    const prettiedDate = getPrettiedHoursAndMinutes(dates as [Dayjs, Dayjs]);
    form.validateFields(['brakeTime']);
    setWorkDayDuration(prettiedDate);
  };

  useEffect(() => {
    if (currentUser && currentRvd && form) {
      form.setFieldValue('employeeId', currentUser.userInfo.id);
      form.setFieldValue('toDate', momentFormatCheck(currentUser.toDate));
      form.setFieldValue('type', currentUser.type);
      if (currentUser.startBreakTime && currentUser.endBreakTime) {
        form.setFieldValue('brakeTime', [
          dayjs(currentUser.startBreakTime, PICKER_TIME_MASK),
          dayjs(currentUser.endBreakTime, PICKER_TIME_MASK),
        ]);
      } else {
        form.setFieldValue('brakeTime', [undefined, undefined]);
      }
      form.setFieldValue('isOnVacation', currentUser.isInVacation);
      form.setFieldValue('workTime', [
        dayjs(currentUser.startTime, PICKER_TIME_MASK),
        dayjs(currentUser.endTime, PICKER_TIME_MASK),
      ]);
      calcDateDiff([dayjs(currentUser.startTime, PICKER_TIME_MASK), dayjs(currentUser.endTime, PICKER_TIME_MASK)]);
    }
  }, [currentUser, currentRvd, form]);

  const onFormSubmitted = (formData: TFormData) => {
    const startBreakTime = formData.brakeTime?.[0];
    const endBreakTime = formData.brakeTime?.[1];
    let breakTime = 0;
    if (startBreakTime && endBreakTime) {
      breakTime = Math.ceil(endBreakTime.diff(startBreakTime) / 1000 / 60);
    }
    if (formData.brakeTime?.[0] && breakTime < 5) {
      notification.warning({ message: 'Перерыв не должен быть меньше 5 минут' });
      return;
    }
    const rvdUser: TUserRvdInfo = {
      toDate: formData.toDate.format(SERVER_DATE_MASK),
      type: formData.type,
      userInfo: employee || currentUser!.userInfo,
      isInVacation: formData.isOnVacation ? true : false,
      startTime: formData.workTime[0].format(PICKER_TIME_MASK),
      endTime: formData.workTime[1].format(PICKER_TIME_MASK),
      startBreakTime: startBreakTime ? formData.brakeTime?.[0].format(PICKER_TIME_MASK) : undefined,
      endBreakTime: endBreakTime ? formData.brakeTime?.[1].format(PICKER_TIME_MASK) : undefined,
    };

    if (currentUser) {
      rvdUser.id = currentUser.id;
    }
    onOk(rvdUser);
    form.resetFields(RVD_FORM_FIELDS);
  };

  return (
    <Drawer
      title="Добавление работы в выходной день"
      onClose={onCloseSidebar}
      open={isOpen}
      width={720}
      extra={
        <Space>
          {remove ? <Button onClick={e => remove(currentUser?.id as number)}>Удалить</Button> : null}
          <Button onClick={onCloseSidebar}>Отмена</Button>
          {currentRvd?.status !== RvdStatuses.LOADED_CPRODUCT ? (
            <Button onClick={submitForm} type="primary">
              Ок
            </Button>
          ) : null}
        </Space>
      }
    >
      <Form
        form={form}
        name="basic"
        layout="vertical"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        style={{ maxWidth: 600 }}
        initialValues={{ remember: true }}
        onFinish={(data: TFormData) => onFormSubmitted(data)}
        autoComplete="off"
      >
        <EmployeeInput
          data={resultEmployees.data}
          getEmployees={getEmployees}
          setEmployee={setEmployee}
          divisionName={employee?.division}
          label="Выберите сотрудника"
          rules={[{ required: true, message: 'Выберите пользователя!' }]}
          className="form-item-rvd"
          onChangeEmployee={() => {}}
          defaultUser={currentUser?.userInfo}
          disabled={currentRvd?.status === RvdStatuses.LOADED_CPRODUCT}
        />
        <div className={style['date-compensation']}>
          <Form.Item label="Дата" name="toDate" rules={[{ required: true, message: 'Выберите дату!' }]}>
            <DatePickerWithDots
              disabled={currentRvd?.status === RvdStatuses.LOADED_CPRODUCT}
              format={PICKER_DATE_MASK}
            />
          </Form.Item>
          <Form.Item
            name="type"
            label="Выберите тип компенсации"
            rules={[{ required: true, message: 'Выберите тип компенсации!' }]}
          >
            <Select disabled={currentRvd?.status === RvdStatuses.LOADED_CPRODUCT}>
              <Select.Option value={RvdCompensationType.HOLIDAY}>{RvdCompensationType.HOLIDAY}</Select.Option>
              <Select.Option value={RvdCompensationType.MONEY}>{RvdCompensationType.MONEY}</Select.Option>
            </Select>
          </Form.Item>
        </div>
        <Form.Item name="isOnVacation" valuePropName="checked">
          <Checkbox disabled={currentRvd?.status === RvdStatuses.LOADED_CPRODUCT} defaultChecked={false}>
            Работа в выходной день в командировке
          </Checkbox>
        </Form.Item>
        <Form.Item
          name="workTime"
          className="form-item-rvd"
          label="Время работы"
          rules={[{ required: true, message: 'Введите время работы!' }]}
        >
          <TimeRangePickerWithDots
            disabled={currentRvd?.status === RvdStatuses.LOADED_CPRODUCT}
            format={PICKER_TIME_MASK}
            placeholder={['Время начала работы', 'Время окончания работы']}
            style={{ width: '100%', maxWidth: '100%' }}
            onChange={calcDateDiff}
            needConfirm={false}
            changeOnScroll={false}
          />
        </Form.Item>
        {workDayDuration ? <p>Продолжительность рабочего дня: {workDayDuration}</p> : null}
        <Form.Item
          name="brakeTime"
          className="form-item-rvd"
          label="Время перерыва"
          rules={[{ required: validateBrakeField(), message: 'Введите время перерыва' }]}
        >
          <TimeRangePickerWithDots
            disabledTime={() => disabledDateTime(form.getFieldValue('workTime'))}
            disabled={currentRvd?.status === RvdStatuses.LOADED_CPRODUCT}
            format={PICKER_TIME_MASK}
            placeholder={['Время начала перерыва', 'Время окончания перерыва']}
            style={{ width: '100%' }}
            needConfirm={false}
            changeOnScroll={false}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default RvdSidebar;
